import { FC, useState } from 'react'
import * as Yup from 'yup'
import { ApolloError, FetchResult, MaybeMasked } from '@apollo/client'
import { Formik, FormikErrors } from 'formik'
import { useTranslation } from 'react-i18next'
import { Col } from 'antd'
import { EmployeeAndTag, EmployeeTagType, RemoveEmployeeTagInput, RemoveEmployeeTagMutation } from '../../../types'
import { PerModal } from '../../../components/custom_components/Modal'
import { Form, Checkbox, FormikTextArea } from '../../../components/formik-component'
import PerText from '../../../components/custom_components/Text'
import PerSpacer from '../../../components/custom_components/Spacer'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { IconIndicator } from '../../../components/custom_components/IconIndicator/IconIndicator'
import { showNotification } from '../../../components/custom_components/Notification/notification'
import './index.less'

const REMOVABLE_LOW_TAG_TYPES: EmployeeTagType[] = [EmployeeTagType.Low, EmployeeTagType.EntryLevelLow]

const findRemovableLowTag = (modalData: EmployeeAndTag) =>
  modalData.tags.find((tag) => !tag.removedDate && REMOVABLE_LOW_TAG_TYPES.includes(tag.type as EmployeeTagType))!

const getMutationErrorMessage = (errors: FetchResult['errors'] | undefined, fallback: string): string => {
  const validationMessage = (errors?.[0]?.extensions as { validationErrors?: Array<{ message?: string }> } | undefined)
    ?.validationErrors?.[0]?.message
  return validationMessage || fallback
}

interface Props {
  isOpen: boolean
  modalData: EmployeeAndTag
  onCancel: () => void
  onSubmit: (formData: RemoveEmployeeTagInput) => Promise<FetchResult<MaybeMasked<RemoveEmployeeTagMutation>>>
}

export const CompanySettingsRankingRemoveLowTagModal: FC<Props> = ({ isOpen, onCancel, onSubmit, modalData }) => {
  const [t] = useTranslation()
  const [modalError, setModalError] = useState<string | undefined>(undefined)
  const tagToRemove = findRemovableLowTag(modalData)
  const confirmErrorMessage = t('companySettingsView.rankingTab.modalRemoveLow.confirmError')

  const createConfirmButtonTooltip = (props: { checkbox: boolean; note: string }): string | undefined => {
    if (props.checkbox && !!props.note && props.note.trim().length >= 2) {
      return undefined
    }
    if (!!props.note && props.note.trim().length > 0 && props.note.trim().length < 2 && props.checkbox) {
      return t('companySettingsView.rankingTab.modalRemoveLow.confirmButtonTooltip.reasonTooShort')
    } else {
      return t('companySettingsView.rankingTab.modalRemoveLow.confirmButtonTooltip.genericMessage')
    }
  }

  const isConfirmButtonDisabled = (errors: FormikErrors<{ checkbox: string | undefined; note: string | undefined }>): boolean => {
    return (!!errors.checkbox && errors.checkbox.length > 0) || (!!errors.note && errors.note.length > 0)
  }

  const handleCancel = () => {
    setModalError(undefined)
    onCancel()
  }

  return (
    <Formik
      initialValues={{ checkbox: false, note: '' }}
      onSubmit={async (values) => {
        setModalError(undefined)
        try {
          const { errors } = await onSubmit({
            employeeId: modalData.id,
            type: tagToRemove.type as EmployeeTagType,
            note: values.note,
          })

          if (errors?.length) {
            setModalError(getMutationErrorMessage(errors, confirmErrorMessage))
            return
          }

          showNotification({
            type: 'success',
            message: t('companySettingsView.rankingTab.modalRemoveLow.notifications.success.title'),
            description: t('companySettingsView.rankingTab.modalRemoveLow.notifications.success.description'),
          })
          handleCancel()
        } catch (error) {
          const apolloError = error as ApolloError
          setModalError(getMutationErrorMessage(apolloError.graphQLErrors, confirmErrorMessage))
        }
      }}
      enableReinitialize={true}
      onReset={() => {
        handleCancel()
      }}
      validateOnMount
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={Yup.object().shape({
        checkbox: Yup.boolean().oneOf(
          [true],
          t('formError.required', { fieldName: t('companySettingsView.rankingTab.modalRemoveLow.checkboxFieldName') }),
        ),
        note: Yup.string()
          .required(t('formError.required', { fieldName: t('companySettingsView.rankingTab.modalRemoveLow.textAreaFieldName') }))
          .trim()
          .min(2, t('companySettingsView.rankingTab.modalRemoveLow.textAreaFieldLength')),
      })}
    >
      {(props) => {
        const { isSubmitting, handleSubmit, handleReset, errors } = props

        return (
          <PerModal
            title={{
              heading: t('companySettingsView.rankingTab.modalRemoveLow.title'),
              subheading: t('companySettingsView.rankingTab.modalRemoveLow.subtitle', {
                employee: `${modalData.firstName} ${modalData.lastName}`,
              }),
            }}
            visible={isOpen}
            footer={{
              cancelBtn: {
                onClick: handleCancel,
              },
              confirmBtn: {
                onClick: handleSubmit,
                disabled: isConfirmButtonDisabled(errors) || isSubmitting || !!modalError,
                isProcessing: { active: isSubmitting },
                tooltip: createConfirmButtonTooltip(props.values),
              },
            }}
            onCancel={handleReset}
          >
            <Form>
              <PerSpacer mt="s" mb="xxl">
                <Checkbox name="checkbox">
                  {t('companySettingsView.rankingTab.modalRemoveLow.checkboxLabel', {
                    employee: `${modalData.firstName} ${modalData.lastName}`,
                  })}
                </Checkbox>
              </PerSpacer>

              <PerText appearance="label-small" display="block">
                {t('companySettingsView.rankingTab.modalRemoveLow.textAreaLabel')}
              </PerText>
              <FormikTextArea
                autoSize={{ minRows: 4, maxRows: 6 }}
                placeholder={t('companySettingsView.rankingTab.modalRemoveLow.textAreaPlaceholder')}
                name="note"
              />
            </Form>
            {!!modalError && (
              <div className="per-add-entry-level-low-tag-modal__status per-add-entry-level-low-tag-modal__status--error per-add-entry-level-low-tag-modal__status--with-top-margin">
                <PerFlexRow align="middle" direction="row">
                  <Col>
                    <IconIndicator icon="warning_fill" iconSize={12} background="error" size="l" />
                  </Col>
                  <Col span={20} style={{ padding: 0, marginLeft: '16px' }}>
                    <PerText fontSize="s">{modalError}</PerText>
                  </Col>
                </PerFlexRow>
              </div>
            )}
          </PerModal>
        )
      }}
    </Formik>
  )
}
